import {
  abortNavigation,
  defineNuxtRouteMiddleware,
  useRequestURL,
  useRuntimeConfig,
} from "nuxt/app";
import type { RouteLocationNormalizedGeneric } from "vue-router";
import { getQueryParamValue } from "@chatfood/core-utils";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useBrandingStore } from "~/stores/branding";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useGuestStore } from "~/stores/guest";
import { SalesChannel } from "~/domain/sales-channels";
import {
  isClient,
  useBugReporter,
  useDirectBackend,
  useGeoIpLocation,
  useLogger,
  usePinia,
  useRequestHeaders,
} from "#imports";

const isValidSalesChannel = (channel: string): channel is SalesChannel => {
  return Object.values(SalesChannel).includes(channel as SalesChannel);
};

const resolveSalesChannel = (
  isDefaultCatering: boolean,
  queryString: RouteLocationNormalizedGeneric["query"],
): SalesChannel | undefined => {
  // Catering is a hard app-level lock — no query param can override it.
  if (isDefaultCatering) {
    return SalesChannel.CATERING;
  }

  const queryStringChannel = getQueryParamValue(queryString, "channel");

  if (queryStringChannel && isValidSalesChannel(queryStringChannel)) {
    return queryStringChannel;
  }

  // Omitting salesChannel lets the BFF fall back to its session-stored value,
  // which preserves non-default channels (widget, instagram, etc.) across page
  // refreshes where the ?channel= query param is no longer in the URL.
  return undefined;
};

const resolveGeoIpLocationHeaders = (headers: Record<string, string>) => {
  return {
    lat: headers["cf-iplatitude"] ?? undefined,
    lng: headers["cf-iplongitude"] ?? undefined,
    city: headers["cf-ipcity"] ?? undefined,
    country: headers["cf-ipcountry"] ?? undefined,
    region: headers["cf-region"] ?? undefined,
    regionCode: headers["cf-region-code"] ?? undefined,
  };
};

export default defineNuxtRouteMiddleware(async (to) => {
  const pinia = usePinia();
  const business = useBusinessStore(pinia);

  if (isClient() || business.isLoaded || process.env.NODE_ENV === "test") {
    return;
  }

  const logger = useLogger();
  const { bootstrapRepo, basketRepo } = useDirectBackend();
  const requestUrl = useRequestURL();
  const config = useRuntimeConfig();
  const headers = useRequestHeaders();

  const featureMatrix = useFeatureMatrixStore(pinia);
  const branding = useBrandingStore(pinia);
  const customer = useCustomerStore(pinia);
  const guest = useGuestStore(pinia);
  const geoIpLocation = useGeoIpLocation(pinia);

  try {
    logger.info("Resolving GeoIP location", { headers });

    const output = resolveGeoIpLocationHeaders(headers);

    logger.info("Resolved GeoIP location", output);

    geoIpLocation.patch({
      isLoaded: true,
      city: output.city,
      country: output.country,
      region: output.region,
      regionCode: output.regionCode,
      coordinate:
        output.lat && output.lng
          ? {
              lat: +output.lat,
              lng: +output.lng,
            }
          : undefined,
    });
  } catch (error: unknown) {
    logger.error("Failed to resolve GeoIP location", {
      error: error instanceof Error ? error.toString() : error,
    });
  }

  try {
    const res = await bootstrapRepo.startWo({
      domain: requestUrl.hostname,
      salesChannel: resolveSalesChannel(
        config.public.defaultCateringChannel,
        to.query,
      ),
      discountCode: getQueryParamValue(to.query, "v"),
    });
    const incomingBusiness = res.business;

    business.patch({
      isLoaded: true,
      accountId: res.accountId,
      id: incomingBusiness.id,
      slug: incomingBusiness.slug,
      name: incomingBusiness.name,
      country: incomingBusiness.country,
      currency: incomingBusiness.currency,
      locale: incomingBusiness.language,
      languages: [incomingBusiness.language, ...incomingBusiness.languages],
      logo: incomingBusiness.logo,
      cover: incomingBusiness.cover,
      hasDelivery: incomingBusiness.hasDelivery,
      hasPickup: incomingBusiness.hasPickup,
      hasCurbside: incomingBusiness.hasCurbside,
      defaultMessagingChannel: incomingBusiness.defaultMessagingChannel,
      defaultOrderingMode: incomingBusiness.defaultOrderingMode,
      menuDisplayLayout: incomingBusiness.menuDisplayLayout,
      whatsapp: incomingBusiness.whatsapp
        ? parseInt(incomingBusiness.whatsapp)
        : undefined,
      gtmContainerId: incomingBusiness.gtmContainerId,
      salesChannel: res.salesChannel,
      siteLinks: res.siteLinks,
    });

    if (res.designTokens) {
      const designTokens = res.designTokens;

      business.patch({
        designTokens: {
          ...designTokens,
          "secondary-tailored-background":
            designTokens["secondary-tailored-background"] ??
            designTokens["ghost-button-background"],
          "secondary-tailored-background-hover":
            designTokens["secondary-tailored-background-hover"] ??
            designTokens["ghost-button-background-hover"],
          "secondary-tailored-text":
            designTokens["secondary-tailored-text"] ??
            designTokens["ghost-button-text"],
          "secondary-tailored-text-hover":
            designTokens["secondary-tailored-text-hover"] ??
            designTokens["ghost-button-text-hover"],
          "secondary-tailored-border-color":
            designTokens["secondary-tailored-border-color"] ??
            designTokens["ghost-button-border-color"],
          "secondary-tailored-border-color-hover":
            designTokens["secondary-tailored-border-color-hover"] ??
            designTokens["ghost-button-border-color-hover"],
        },
      });
    }

    if (res.customStyles?.cssPath) {
      business.patch({
        styles: { customCssSheet: res.customStyles.cssPath },
      });
    }

    if (res.countrySettings) {
      business.patch({
        config: {
          ...business.config,
          isPostalCodeRequired: res.countrySettings.isPostalCodeRequired,
        },
      });
    }

    if (res.featureMatrixToggles.length) {
      featureMatrix.patch({
        toggles: res.featureMatrixToggles as typeof featureMatrix.toggles,
      });
    }

    if (res.tipSettings) {
      business.patch({
        config: {
          ...business.config,
          acceptsDriverTips: res.tipSettings.acceptsDriverTips,
          isTipsEnabled: res.tipSettings.isEnabled,
          tipPercentages: res.tipSettings.tipPercentages,
        },
      });
    }

    if (res.brandingMedias) {
      branding.setMedias(res.brandingMedias);
    }

    const incomingCustomer = res.customer;
    if (incomingCustomer) {
      try {
        customer.patch({
          id: incomingCustomer.id,
          name: incomingCustomer.name,
          email: incomingCustomer.email,
          countryCode: parseInt(incomingCustomer.countryCode),
          phoneNumber: parseInt(incomingCustomer.phoneNumber),
          isLogged: true,
        });

        await Promise.all([
          customer.syncAddresses(incomingBusiness.id),
          customer.loadPrefences(incomingBusiness.id),
        ]);
      } catch {
        // silence error
      }
    }

    if (res.guest && !incomingCustomer) {
      guest.patch({
        isLoaded: true,
        name: res.guest.name || undefined,
        email: res.guest.email || undefined,
        phoneNumber: res.guest.phoneNumber || undefined,
        countryCode: res.guest.countryCode || undefined,
        address: res.guest.address
          ? {
              lat: res.guest.address.lat,
              lng: res.guest.address.lng,
              location: res.guest.address.location ?? "",
              number: res.guest.address.number,
              city: res.guest.address.city,
              country: res.guest.address.country,
              zipCode: res.guest.address.zipCode,
              instruction: res.guest.address.instruction,
            }
          : undefined,
      });
    }

    try {
      const basket = useBasketStore(pinia);
      const summaries = await basketRepo.getBaskets();
      basket.setSummaries(summaries);
    } catch (error) {
      logger.warning(
        "Failed to fetch basket summaries, falling back to POST /baskets",
        {
          error: error instanceof Error ? error.toString() : error,
        },
      );
    }
  } catch (error: unknown) {
    if (
      error &&
      typeof error === "object" &&
      "statusCode" in error &&
      (error as { statusCode: number }).statusCode === 404
    ) {
      return abortNavigation({
        message: "Business not found",
        statusCode: 404,
      });
    }

    useBugReporter().report(error, {
      tags: {
        "code.flag": "middleware-setup",
      },
    });

    return abortNavigation({
      message: "Unexpected error trying to fetch business by domain",
      statusCode: 500,
    });
  }
});
